import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  IconButton,
  Chip,
  Divider,
  Snackbar,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  Tooltip,
} from "@mui/material";

import { createTheme, ThemeProvider } from "@mui/material/styles";
import { apiRequest } from "../../fetchSetup";

import {
  DeleteOutline,
  Refresh,
  Search,
  ReceiptLong,
  CurrencyRupee,
  AccessTime,
  CheckCircleOutline,
  CleaningServices,
} from "@mui/icons-material";

import bg from "../Logos/med_icon3.png";

// ─────────────────────────────────────────────
// THEME — matches WeeklyNotificationManagement
// ─────────────────────────────────────────────
const hubTheme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: "#006D77",
      light: "#00949f",
      dark: "#004d55",
      contrastText: "#fff",
    },
    secondary: {
      main: "#E29578",
    },
    background: {
      default: "#F1F5F9",
      paper: "#ffffff",
    },
    text: {
      primary: "#0f172a",
      secondary: "#64748b",
    },
  },
  typography: {
    fontFamily: "'DM Sans', 'Inter', sans-serif",
    h5: {
      fontFamily: "'Sora', sans-serif",
      fontWeight: 800,
    },
    h6: {
      fontFamily: "'Sora', sans-serif",
      fontWeight: 700,
    },
  },
  shape: {
    borderRadius: 12,
  },
});

const DRAWER_WIDTH = 280;
const MOBILE_BAR_H = 56;

const PAYMENT_API_BASE = process.env.REACT_APP_FAST_API;

const STATUS_TABS = [
  { key: "pending", label: "Pending" },
  { key: "fulfilled", label: "Fulfilled" },
  { key: "all", label: "All" },
];

const MIN_BULK_MINUTES = 10;

function timeAgo(dateStr) {
  if (!dateStr) return "—";
  const diffMs = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export default function PendingOrdersReview() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("pending");
  const [search, setSearch] = useState("");

  // single-delete dialog state
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [paidConflict, setPaidConflict] = useState(null); // message when backend returns 409

  // bulk cleanup dialog state
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkMinutes, setBulkMinutes] = useState(60);
  const [bulkRunning, setBulkRunning] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // ─────────────────────────────────────────────
  // FETCH
  // ─────────────────────────────────────────────
  const fetchOrders = async () => {
    try {
      setLoading(true);
      const statusParam = tab === "all" ? "" : `?status=${tab}`;
      const response = await apiRequest(
        `${PAYMENT_API_BASE}/pending/pending-orders${statusParam}`
      );
      const data = await response.json();
      setOrders(data.orders || data || []);
    } catch (error) {
      console.error(error);
      setSnackbar({
        open: true,
        message: "Failed to load pending orders",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  // ─────────────────────────────────────────────
  // DELETE (single)
  // ─────────────────────────────────────────────
  const closeDeleteDialog = () => {
    setConfirmTarget(null);
    setPaidConflict(null);
  };

  const handleDeleteConfirmed = async (force = false) => {
    if (!confirmTarget) return;
    const orderId = confirmTarget.razorpay_order_id || confirmTarget.id;

    try {
      setDeletingId(orderId);
      const response = await apiRequest(
        `${PAYMENT_API_BASE}/pending/pending-orders/${orderId}${force ? "?force=true" : ""}`,
        { method: "DELETE" }
      );

      if (response.status === 409) {
        // Razorpay says this payment was captured — keep the dialog open
        const data = await response.json().catch(() => ({}));
        setPaidConflict(
          data.detail ||
            "This payment was captured. Deleting it may leave the customer charged with no order."
        );
        return;
      }

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || data.error || "Failed to delete order");
      }

      setOrders((prev) =>
        prev.filter((o) => (o.razorpay_order_id || o.id) !== orderId)
      );
      setSnackbar({
        open: true,
        message: "Pending order deleted",
        severity: "success",
      });
      closeDeleteDialog();
    } catch (error) {
      console.error(error);
      setSnackbar({
        open: true,
        message: error.message || "Failed to delete order",
        severity: "error",
      });
      closeDeleteDialog();
    } finally {
      setDeletingId(null);
    }
  };

  // ─────────────────────────────────────────────
  // DELETE (bulk stale + unpaid)
  // ─────────────────────────────────────────────
  const handleBulkCleanup = async () => {
    try {
      setBulkRunning(true);
      const response = await apiRequest(
        `${PAYMENT_API_BASE}/pending/pending-orders?older_than_minutes=${bulkMinutes}`,
        { method: "DELETE" }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.detail || "Bulk cleanup failed");

      setSnackbar({
        open: true,
        message: `Deleted ${data.deleted}. Skipped ${data.skipped_paid} that were paid${
          data.errors ? `, ${data.errors} errors` : ""
        }.`,
        severity: data.errors ? "warning" : "success",
      });
      setBulkOpen(false);
      fetchOrders();
    } catch (error) {
      console.error(error);
      setSnackbar({
        open: true,
        message: error.message || "Bulk cleanup failed",
        severity: "error",
      });
    } finally {
      setBulkRunning(false);
    }
  };

  const filteredOrders = orders.filter((o) => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return (
      (o.razorpay_order_id || "").toLowerCase().includes(q) ||
      (o.customer_name || "").toLowerCase().includes(q) ||
      (o.phone || "").toLowerCase().includes(q) ||
      (o.service_type || "").toLowerCase().includes(q)
    );
  });

  const confirmTargetId =
    confirmTarget?.razorpay_order_id || confirmTarget?.id;

  return (
    <ThemeProvider theme={hubTheme}>
      <link
        href="https://fonts.googleapis.com/css2?family=Sora:wght@700;800;900&family=DM+Sans:wght@400;500;600;700&display=swap"
        rel="stylesheet"
      />

      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: "#F1F5F9",
          ml: { xs: 0, md: `${DRAWER_WIDTH}px` },
          pt: { xs: `${MOBILE_BAR_H}px`, md: 0 },
          width: {
            xs: "100%",
            md: `calc(100% - ${DRAWER_WIDTH}px)`,
          },
          overflowX: "hidden",
        }}
      >
        {/* HEADER */}
        <Box
          sx={{
            backgroundImage: `
              linear-gradient(135deg, rgba(0,109,119,0.9) 0%, rgba(0,169,181,0.68) 100%),
              url(${bg})
            `,
            backgroundSize: "cover",
            backgroundPosition: "center",
            px: { xs: 2, sm: 4 },
            pt: { xs: 2.5, sm: 3 },
            pb: 3,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-end",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Box>
            <Typography
              sx={{
                fontFamily: "'Sora',sans-serif",
                fontWeight: 900,
                fontSize: { xs: 18, sm: 22, md: 26 },
                color: "#fff",
              }}
            >
              🧾 Pending Orders Review
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: 11, sm: 13 },
                color: "rgba(255,255,255,0.75)",
                mt: 0.5,
              }}
            >
              Payments staged but not yet confirmed into a booking or order
            </Typography>
          </Box>

          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              onClick={() => setBulkOpen(true)}
              startIcon={<CleaningServices />}
              sx={{
                color: "#fff",
                bgcolor: "rgba(255,255,255,0.12)",
                "&:hover": { bgcolor: "rgba(255,255,255,0.2)" },
              }}
            >
              Clear stale unpaid
            </Button>
            <Button
              onClick={fetchOrders}
              startIcon={<Refresh />}
              sx={{
                color: "#fff",
                bgcolor: "rgba(255,255,255,0.12)",
                "&:hover": { bgcolor: "rgba(255,255,255,0.2)" },
              }}
            >
              Refresh
            </Button>
          </Box>
        </Box>

        {/* CONTENT */}
        <Box sx={{ p: { xs: 2, md: 4 } }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 2,
              mb: 3,
            }}
          >
            <Tabs
              value={tab}
              onChange={(e, v) => setTab(v)}
              sx={{
                minHeight: 40,
                "& .MuiTab-root": {
                  minHeight: 40,
                  textTransform: "none",
                  fontWeight: 700,
                },
              }}
            >
              {STATUS_TABS.map((t) => (
                <Tab key={t.key} value={t.key} label={t.label} />
              ))}
            </Tabs>

            <TextField
              size="small"
              placeholder="Search name, phone, order id"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 260, bgcolor: "#fff", borderRadius: 2 }}
            />
          </Box>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
              <CircularProgress />
            </Box>
          ) : filteredOrders.length === 0 ? (
            <Card
              sx={{
                borderRadius: 2,
                boxShadow: "0 4px 18px rgba(0,0,0,0.06)",
                py: 6,
                textAlign: "center",
              }}
            >
              <CheckCircleOutline
                sx={{ fontSize: 40, color: "#94a3b8", mb: 1 }}
              />
              <Typography sx={{ color: "#64748b" }}>
                Nothing here — every staged payment is accounted for.
              </Typography>
            </Card>
          ) : (
            <Grid container spacing={3}>
              {filteredOrders.map((order) => {
                const orderId = order.razorpay_order_id || order.id;
                const isFulfilled = order.status === "fulfilled";

                return (
                  <Grid item xs={12} md={6} lg={4} key={orderId}>
                    <Card
                      sx={{
                        borderRadius: 2,
                        height: "100%",
                        boxShadow: "0 4px 18px rgba(0,0,0,0.06)",
                        border: isFulfilled
                          ? "1px solid #d1fae5"
                          : "1px solid #fde68a",
                      }}
                    >
                      <CardContent>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                            mb: 2,
                          }}
                        >
                          <ReceiptLong
                            sx={{ color: "#006D77", fontSize: 32 }}
                          />
                          <Chip
                            size="small"
                            label={isFulfilled ? "FULFILLED" : "PENDING"}
                            color={isFulfilled ? "success" : "warning"}
                          />
                        </Box>

                        <Typography
                          sx={{ fontWeight: 800, fontSize: 16, mb: 0.5 }}
                        >
                          {order.customer_name || "Unknown customer"}
                        </Typography>
                        <Typography
                          sx={{ color: "#64748b", fontSize: 13, mb: 2 }}
                        >
                          {order.phone || "No phone on record"}
                        </Typography>

                        <Divider sx={{ my: 1.5 }} />

                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            mb: 1,
                          }}
                        >
                          <CurrencyRupee
                            sx={{ fontSize: 16, color: "#94a3b8" }}
                          />
                          <Typography sx={{ fontSize: 14 }}>
                            <b>₹{Number(order.amount || 0).toFixed(2)}</b>
                            {"  "}
                            <span style={{ color: "#94a3b8" }}>
                              ({order.service_type || "unknown"})
                            </span>
                          </Typography>
                        </Box>

                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            mb: 1,
                          }}
                        >
                          <AccessTime
                            sx={{ fontSize: 16, color: "#94a3b8" }}
                          />
                          <Typography
                            sx={{ fontSize: 13, color: "#64748b" }}
                          >
                            Staged {timeAgo(order.created_at)}
                          </Typography>
                        </Box>

                        <Typography
                          sx={{
                            fontSize: 12,
                            color: "#94a3b8",
                            wordBreak: "break-all",
                            mb: 2,
                          }}
                        >
                          {orderId}
                        </Typography>

                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                          }}
                        >
                          <Tooltip
                            title={
                              isFulfilled
                                ? "Already fulfilled — deleting only removes this staging record, not the real order"
                                : "Delete this staged payment record"
                            }
                          >
                            <span>
                              <IconButton
                                color="error"
                                disabled={deletingId === orderId}
                                onClick={() => setConfirmTarget(order)}
                              >
                                <DeleteOutline />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Box>

        {/* DELETE CONFIRMATION */}
        <Dialog open={!!confirmTarget} onClose={closeDeleteDialog}>
          <DialogTitle sx={{ fontWeight: 800 }}>
            {paidConflict
              ? "This payment was captured"
              : "Delete this pending order?"}
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              {paidConflict ? (
                paidConflict
              ) : confirmTarget?.status === "fulfilled" ? (
                <>
                  This payment was already fulfilled, so a real booking or
                  order exists for it. Deleting only removes this staging
                  record and does not affect the created order.
                </>
              ) : (
                <>
                  This removes the staged record for order{" "}
                  <b>{confirmTargetId}</b>. The server checks Razorpay first
                  and blocks the delete if the payment was actually captured.
                </>
              )}
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={closeDeleteDialog}>Cancel</Button>
            {paidConflict ? (
              <Button
                color="error"
                variant="contained"
                onClick={() => handleDeleteConfirmed(true)}
                disabled={!!deletingId}
              >
                Delete anyway
              </Button>
            ) : (
              <Button
                color="error"
                variant="contained"
                onClick={() => handleDeleteConfirmed(false)}
                disabled={!!deletingId}
              >
                Delete
              </Button>
            )}
          </DialogActions>
        </Dialog>

        {/* BULK CLEANUP */}
        <Dialog
          open={bulkOpen}
          onClose={() => !bulkRunning && setBulkOpen(false)}
        >
          <DialogTitle sx={{ fontWeight: 800 }}>
            Clear stale unpaid orders
          </DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              Deletes pending records older than the time below that Razorpay
              confirms were never paid. Anything paid is skipped and left for
              reconciliation.
            </DialogContentText>
            <TextField
              type="number"
              label="Older than (minutes)"
              value={bulkMinutes}
              onChange={(e) =>
                setBulkMinutes(
                  Math.max(MIN_BULK_MINUTES, Number(e.target.value) || MIN_BULK_MINUTES)
                )
              }
              fullWidth
              size="small"
              helperText={`Minimum ${MIN_BULK_MINUTES} minutes`}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setBulkOpen(false)} disabled={bulkRunning}>
              Cancel
            </Button>
            <Button
              color="error"
              variant="contained"
              onClick={handleBulkCleanup}
              disabled={bulkRunning}
            >
              {bulkRunning ? "Cleaning…" : "Clear stale"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* SNACKBAR */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert severity={snackbar.severity} variant="filled">
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </ThemeProvider>
  );
}